import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Mail, Phone, MapPin, Home, Lock, Eye, EyeOff, ArrowLeft, CheckCircle } from 'lucide-react';
import MainService from '../services/mainservice';
import { useToast } from '../contexts/ToastContext';

const Register = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  
  const [currentStep, setCurrentStep] = useState(0); // 0: Mobile, 1: OTP, 2: Form
  const [loading, setLoading] = useState(false);
  const [emirates, setEmirates] = useState([]);
  const [locations, setLocations] = useState([]);
  const [companyProfile, setCompanyProfile] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [locationSearchQuery, setLocationSearchQuery] = useState('');
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  
  const [formData, setFormData] = useState({
    mobileNo1: '',
    mobileNo2: '',
    name: '',
    emailId: '',
    emirateId: '',
    locationId: '',
    locationName: '',
    address: '',
    poBox: '',
    userName: '',
    password: '',
    confirmPassword: '',
    otp: '',
    realOtp: 0
  });

  useEffect(() => {
    loadEmirates();
    loadCompanyProfile();
  }, []);

  useEffect(() => {
    if (formData.emirateId) {
      loadLocations(formData.emirateId);
    }
  }, [formData.emirateId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showLocationDropdown && !event.target.closest('.location-dropdown-container')) {
        setShowLocationDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showLocationDropdown]);

  const loadEmirates = async () => {
    try {
      const response = await MainService.EmiratesInDropDown();
      if (response && response.data) {
        setEmirates(response.data);
      }
    } catch (error) {
      console.error('Error loading emirates:', error);
    }
  };

  const loadCompanyProfile = async () => {
    try {
      const response = await MainService.GetCompanyProfile();
      if (response && response.data) {
        setCompanyProfile(response.data);
      }
    } catch (error) {
      console.error('Error loading company profile:', error);
    }
  };

  const loadLocations = async (emirateId) => {
    try {
      const response = await MainService.LocationsAutoCompleteByEmirate({ EmirateId: emirateId });
      if (response && response.data) {
        setLocations(response.data);
      }
    } catch (error) {
      console.error('Error loading locations:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEmirateSelect = (emirateId) => {
    const selectedEmirate = emirates.find(e => e.Id === emirateId);
    setFormData(prev => ({
      ...prev,
      emirateId: emirateId,
      locationId: '',
      locationName: ''
    }));
    setLocationSearchQuery('');
    setShowLocationDropdown(false);
  };

  const handleLocationSearch = (query) => {
    setLocationSearchQuery(query);
    setShowLocationDropdown(true);
  };

  const handleLocationSelect = (location) => {
    setFormData(prev => ({
      ...prev,
      locationId: location.Id,
      locationName: location.Name
    }));
    setLocationSearchQuery(location.Name);
    setShowLocationDropdown(false);
  };

  const filteredLocations = locations.filter(location =>
    location.Name.toLowerCase().includes(locationSearchQuery.toLowerCase())
  );

  const checkMobileNumber = async () => {
    if (!formData.mobileNo1) {
      showError('Please enter mobile number');
      return;
    }

    if (formData.mobileNo1.length !== 10 && formData.mobileNo1.length !== 9) {
      showError('Please enter a valid mobile number (9-10 digits)');
      return;
    }

    try {
      setLoading(true);
      const response = await MainService.CheckMobileNoRegister(formData.mobileNo1);
      
      if (response.data === 0) {
        showError('Network error occurred');
      } else if (response.data === 2) {
        showError('Mobile number already registered');
      } else {
        showSuccess('OTP sent to your mobile number');
        setFormData(prev => ({ ...prev, realOtp: response.data }));
        setCurrentStep(1);
      }
    } catch (error) {
      console.error('Error checking mobile:', error);
      showError('Network error occurred');
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = () => {
    if (!formData.otp) {
      showError('Please enter OTP');
      return;
    }

    if (formData.otp !== formData.realOtp.toString()) {
      showError('Invalid OTP');
      return;
    }

    setCurrentStep(2);
  };

  const registerClient = async () => {
    // Validation
    if (!formData.name) {
      showError('Please enter your name');
      return;
    }
    if (!formData.emirateId) {
      showError('Please select emirate');
      return;
    }
    if (!formData.locationId) {
      showError('Please select location');
      return;
    }
    if (!formData.address) {
      showError('Please enter address');
      return;
    }
    if (!formData.password) {
      showError('Please enter password');
      return;
    }
    if (!formData.confirmPassword) {
      showError('Please confirm password');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      showError('Passwords do not match');
      return;
    }

    try {
      setLoading(true);
      
      const registrationData = {
        Name: formData.name,
        MobileNo1: formData.mobileNo1,
        MobileNo2: formData.mobileNo2,
        EmailId: formData.emailId,
        Photo: '',
        EmirateId: formData.emirateId,
        LocationId: formData.locationId,
        Password: formData.password,
        CPassword: formData.confirmPassword,
        UserName: formData.userName,
        Address: formData.address,
        POBOX: formData.poBox
      };

      const response = await MainService.RegisterClient(registrationData);
      
      if (response.data) {
        showSuccess('Registration successful! Please login to continue.');
        navigate('/login');
      } else {
        showError('Registration failed. Please try again.');
      }
    } catch (error) {
      console.error('Error registering client:', error);
      showError('Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 flex items-center justify-center p-4">
      <div className="w-full max-w-6xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          {/* Left Side - Welcome */}
          <div className="hidden lg:block">
            <div className="bg-gradient-to-br from-blue-600 to-green-600 rounded-3xl p-12 text-white text-center">
              <div className="mb-8">
                <img src="/images/dboy.png" alt="Delivery" className="w-32 h-32 mx-auto mb-6" />
                <h1 className="text-4xl font-bold mb-4">Hello, Friend!</h1>
                <p className="text-xl mb-2">If you are an existing user</p>
                <h3 className="text-2xl font-semibold mb-8">Login to your account now!</h3>
                <button
                  onClick={() => navigate('/login')}
                  className="bg-white text-primary-600 font-semibold py-3 px-8 rounded-xl hover:bg-gray-100 transition-colors duration-200"
                >
                  Login Now
                </button>
              </div>
              <div className="mt-8 text-sm opacity-90">
                <p>Client Portal | Powered By <a href="https://ainsoftsolutions.com" target="_blank" rel="noopener noreferrer" className="underline">Ainsoft Solutions</a></p>
              </div>
            </div>
          </div>

          {/* Right Side - Registration Form */}
          <div className="bg-white rounded-3xl shadow-2xl p-8">
            <div className="text-center mb-8">
              {companyProfile.Logo && (
                <img src={companyProfile.Logo} alt="Company Logo" className="w-24 h-24 mx-auto mb-4" />
              )}
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Welcome to {companyProfile.Name || 'Client Portal'}
              </h2>
              <p className="text-gray-600">Create your account</p>
            </div>

            {/* Progress Indicator */}
            <div className="flex items-center justify-center mb-8">
              <div className="flex items-center space-x-4">
                {[0, 1, 2].map((step) => (
                  <div key={step} className="flex items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                      currentStep >= step 
                        ? 'bg-primary-600 text-white' 
                        : 'bg-gray-200 text-gray-600'
                    }`}>
                      {currentStep > step ? <CheckCircle className="w-4 h-4" /> : step + 1}
                    </div>
                    {step < 2 && (
                      <div className={`w-12 h-1 mx-2 ${
                        currentStep > step ? 'bg-primary-600' : 'bg-gray-200'
                      }`} />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Step 0: Mobile Number */}
            {currentStep === 0 && (
              <div className="space-y-6">
                <div className="text-center">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Enter Your Mobile Number</h3>
                  <p className="text-gray-600">We'll send you an OTP to verify your number</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
                    <Phone className="w-4 h-4 mr-2 text-primary-600" />
                    Mobile Number
                  </label>
                  <input
                    type="tel"
                    value={formData.mobileNo1}
                    onChange={(e) => handleInputChange('mobileNo1', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
                    placeholder="Enter your mobile number"
                  />
                </div>

                <button
                  onClick={checkMobileNumber}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 disabled:from-gray-400 disabled:to-gray-500 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 flex items-center justify-center space-x-2"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : (
                    <>
                      <span>Continue</span>
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Step 1: OTP Verification */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div className="text-center">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Verify OTP</h3>
                  <p className="text-gray-600">Enter the OTP sent to {formData.mobileNo1}</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
                    <Lock className="w-4 h-4 mr-2 text-primary-600" />
                    OTP Code
                  </label>
                  <input
                    type="text"
                    value={formData.otp}
                    onChange={(e) => handleInputChange('otp', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 text-center text-lg tracking-widest"
                    placeholder="Enter OTP"
                    maxLength="6"
                  />
                </div>

                <div className="flex space-x-4">
                  <button
                    onClick={goBack}
                    className="flex-1 bg-gray-100 text-gray-700 font-semibold py-3 px-6 rounded-xl hover:bg-gray-200 transition-colors duration-200 flex items-center justify-center space-x-2"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    <span>Back</span>
                  </button>
                  <button
                    onClick={verifyOTP}
                    disabled={loading}
                    className="flex-1 bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 disabled:from-gray-400 disabled:to-gray-500 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 flex items-center justify-center space-x-2"
                  >
                    {loading ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    ) : (
                      <span>Verify</span>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Registration Form */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div className="text-center">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Complete Your Registration</h3>
                  <p className="text-gray-600">Fill in your details to create your account</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
                      <User className="w-4 h-4 mr-2 text-primary-600" />
                      Full Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
                      placeholder="Enter your full name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
                      <Phone className="w-4 h-4 mr-2 text-primary-600" />
                      Mobile No 2
                    </label>
                    <input
                      type="tel"
                      value={formData.mobileNo2}
                      onChange={(e) => handleInputChange('mobileNo2', e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
                      placeholder="Enter second mobile number"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
                    <Mail className="w-4 h-4 mr-2 text-primary-600" />
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={formData.emailId}
                    onChange={(e) => handleInputChange('emailId', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
                    placeholder="Enter your email address"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
                      <MapPin className="w-4 h-4 mr-2 text-primary-600" />
                      Emirate *
                    </label>
                    <select
                      value={formData.emirateId}
                      onChange={(e) => handleEmirateSelect(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
                    >
                      <option value="">Select Emirate</option>
                      {emirates.map((emirate) => (
                        <option key={emirate.Id} value={emirate.Id}>
                          {emirate.Name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="location-dropdown-container relative">
                    <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
                      <MapPin className="w-4 h-4 mr-2 text-primary-600" />
                      Location *
                    </label>
                    <input
                      type="text"
                      value={locationSearchQuery}
                      onChange={(e) => handleLocationSearch(e.target.value)}
                      onFocus={() => setShowLocationDropdown(true)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
                      placeholder="Search and select location"
                    />
                    {showLocationDropdown && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                        {filteredLocations.map((location) => (
                          <div
                            key={location.Id}
                            onClick={() => handleLocationSelect(location)}
                            className="px-4 py-3 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                          >
                            {location.Name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
                    <Home className="w-4 h-4 mr-2 text-primary-600" />
                    Address *
                  </label>
                  <textarea
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 resize-none"
                    rows="3"
                    placeholder="Enter your complete address"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
                      <Home className="w-4 h-4 mr-2 text-primary-600" />
                      PO Box
                    </label>
                    <input
                      type="text"
                      value={formData.poBox}
                      onChange={(e) => handleInputChange('poBox', e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
                      placeholder="Enter PO Box number"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
                      <User className="w-4 h-4 mr-2 text-primary-600" />
                      Username
                    </label>
                    <input
                      type="text"
                      value={formData.userName}
                      onChange={(e) => handleInputChange('userName', e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
                      placeholder="Choose a username"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
                      <Lock className="w-4 h-4 mr-2 text-primary-600" />
                      Password *
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={(e) => handleInputChange('password', e.target.value)}
                        className="w-full px-4 py-3 pr-12 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
                        placeholder="Enter password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
                      <Lock className="w-4 h-4 mr-2 text-primary-600" />
                      Confirm Password *
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={formData.confirmPassword}
                        onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                        className="w-full px-4 py-3 pr-12 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
                        placeholder="Confirm password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex space-x-4">
                  <button
                    onClick={goBack}
                    className="flex-1 bg-gray-100 text-gray-700 font-semibold py-3 px-6 rounded-xl hover:bg-gray-200 transition-colors duration-200 flex items-center justify-center space-x-2"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    <span>Back</span>
                  </button>
                  <button
                    onClick={registerClient}
                    disabled={loading}
                    className="flex-1 bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 disabled:from-gray-400 disabled:to-gray-500 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 flex items-center justify-center space-x-2"
                  >
                    {loading ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    ) : (
                      <span>Register</span>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Login Link */}
            <div className="text-center mt-6">
              <p className="text-gray-600">
                Already have an account?{' '}
                <button
                  onClick={() => navigate('/login')}
                  className="text-primary-600 hover:text-primary-700 font-semibold underline"
                >
                  Login here
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
